"use strict";
/* General JS methods and variables to be used in any other theme's js file */

/*==================================================================
▐	1. IMPORTS and EXPORTS
===================================================================*/
import debounce from 'lodash.debounce';
import throttle from 'lodash.throttle';
import {gsap} from 'gsap';

export const gh = {}; // general helper variables
(function($) {
/*==================================================================
▐	2. GENERAL METHODS
===================================================================*/
/**
 * Runs the callback (with specified delays) on certain events
 * @param {Object} events - list of events
 * @param {callback} callback
 * @param elem - Any dom element
 */
gh.doOnEvent = ( events, callback, elem = window ) => {
	if ( typeof events !== 'object' ) {
		return
	}

	Object.keys( events ).forEach( ( key ) => {
		let delay = (events[ key ] ? events[ key ] : 0);

		switch (key) {
			case 'start':
				setTimeout( () => {
					callback();
				}, delay );

				break;
			case 'load':
				window.addEventListener( 'load', () => {
					setTimeout( () => {
						callback();
					}, delay );
				} );
				break;
			case 'scroll':
				document.addEventListener( 'scroll', throttle( () => {
					callback();
				}, delay ) );
				break;
			case 'resize':
				window.addEventListener( 'resize', debounce( () => {
					callback();
				}, delay ) );
				break;
			case 'orientationchange':
				// NEW method (not supported in Safari)
				if ( screen.orientation ) {
					screen.orientation.addEventListener( 'change', function( e ) {
						// delay because width and height of window changes after orientation event
						setTimeout( function() {
							callback();
						}, (events[ key ] ? events[ key ] : 250) );
					} );
					break;
				} // IF
				// OLD method (still supported in Safari, but not in Chrome's Dev tools mobile mode)
				window.addEventListener( 'orientationchange', () => {
					// delay because width and height of window changes after orientation event
					setTimeout( function() {
						callback();
					}, (events[ key ] ? events[ key ] : 250) );
				}, false );
				break;
			default:
				elem.addEventListener( key, () => {
					setTimeout( () => {
						callback();
					}, delay );
				} );
				break;
		}
	} );
}; // FNC

/**
 * Detect iOS device (which is also a mobile device)
 */
gh.detectIOS = () => {
	return [
			'iPad Simulator',
			'iPhone Simulator',
			'iPod Simulator',
			'iPad',
			'iPhone',
			'iPod',
		].includes( (navigator?.platform || navigator?.userAgentData?.platform) )
		// iPad on iOS 13-15 detection
		|| ((navigator?.platform.includes( 'Mac' ) || navigator.userAgent.includes( "Mac" )) && 'ontouchend' in document)
};

/**
 * Adds event which fires when media query breakpoint changes
 */
gh.addMatchMediaOnChangeEvent = (mediaQuery, callBack) => {
	const matchMedia = window.matchMedia( mediaQuery );
	matchMedia.addEventListener( 'change', event => {
		callBack( event );
	} );
	return matchMedia.matches;

};

/**
 * Validates Email address for an input field
 */
gh.validateEmailAddress = (inputValue) => {
	let result = true;
	let pattern = /^([a-zA-Z0-9_.+-])+\@(([a-zA-Z0-9-])+\.)+([a-zA-Z0-9]{2,4})+$/;
	if (!pattern.test(inputValue)) {
		result = false;
	}

	return result;
}

/*==================================================================
▐	3. SET VARIABLES
===================================================================*/
gh.html     = window.document.documentElement;
gh.body     = document.body;
gh.isIOS    = gh.detectIOS();
gh.isMobileDevice = ((navigator.userAgent.includes( 'Mobi' ) || navigator.userAgent.includes( 'Androi' )) && (('ontouchstart' in window) ||
	(navigator.maxTouchPoints > 0) || (navigator.msMaxTouchPoints > 0))) || gh.isIOS;

// Set mobile/desktop design var
gh.isDesktopDesign = gh.addMatchMediaOnChangeEvent( '(min-width: 1200px)', ( event ) => {
	gh.isDesktopDesign = event.matches;
	document.dispatchEvent( new CustomEvent( 'designChange' ) );
} );

// Set mobile/desktop design var
gh.isTabletDesign = gh.addMatchMediaOnChangeEvent( '(min-width: 768px)', ( event ) => {
	gh.isTabletDesign = event.matches;
	document.dispatchEvent( new CustomEvent( 'designChangeTablet' ) );
} );

// Set mobile/desktop device class
if ( gh.isMobileDevice ) {
	gh.html.classList.add( 'mobile-device' )
} else {
	gh.html.classList.add( 'desktop-device' )
}

/*==================================================================
▐	3. SPECIFIC METHODS
===================================================================*/

/**
 * Outputs HTML/SVG for owl carousel navigation arrows
 * @param {string} type
 * @param {boolean} direction_left
 * @param {boolean|string} mobileClass
 * @param {boolean|string} desktopClass
 */
gh.owlSliderCustomNavArrows = ( type, direction_left = true, mobileClass = 'd-md-none color-accent', desktopClass = 'd-none d-md-inline-block color-accent' ) => {

	let
		desktopLeft  = '<svg width="40" height="28" class="svg-icon ' + desktopClass + '"><use xlink:href="#se-icons__arrow-left"/></svg>',
		desktopRight = '<svg width="40" height="28" class="svg-icon flip-x ' + desktopClass + '"><use xlink:href="#se-icons__arrow-left"/></svg>',

		mobileLeft   = '<svg width="21" height="13" class="svg-icon ' + mobileClass + '"><use xlink:href="#se-icons__arrow-left-small"/></svg>',
		mobileRight  = '<svg width="21" height="13" class="svg-icon flip-x ' + mobileClass + '"><use xlink:href="#se-icons__arrow-left-small"/></svg>';

	// Mobile or Desktop arrow type
	switch (type) {
		case 'both':
			return direction_left ? desktopLeft + mobileLeft : desktopRight + mobileRight;
		// break;
		case 'mobile':
			return direction_left ? mobileLeft : mobileRight;
		// break;
		default: // desktop
			return direction_left ? desktopLeft : desktopRight;
		// break;
	} // SWITCH

} // FNC


/**
 * Inits the row navigation slider
 * @param {Object} slider_el element selected by jQuery
 * @param {Array} customArrows custom nav arrows
 */
gh.rowNavSliderInit = ( slider_el, customArrows, customOptions = {} ) => {
	if ( !slider_el.length ) return;

	const navArrows = customArrows ? customArrows : [
		gh.owlSliderCustomNavArrows( 'mobile', true, 'color-accent', false ),
		gh.owlSliderCustomNavArrows( 'mobile', false, 'color-accent', false ),
	];


	// Options
	let options = {
		loop:      false,
		margin:    71,
		autoWidth: true,
		nav:       true,
		dots:      false,
		mouseDrag: false,
		slideBy:   3,
		// items: 3,
		navContainerClass: 'owl-nav position-absolute top-50 translate-middle-y mt-0 d-flex justify-content-between',
		navText:           navArrows,
	};
	// merge option objects
	if ( Object.keys( customOptions ).length ) {
		options = { ...options, ...customOptions };
	}


	function initOrDestroySlider() {
		if ( gh.isTabletDesign ) {
			navRowSliderRun( true ); // init
			return;
		}
		navRowSliderRun(); // destroy
	}

	function navRowSliderRun( init ) {
		// Destroy
		if ( !init ) {
			slider_el.trigger('destroy.owl.carousel').removeClass('owl-carousel owl-theme owl-loaded owl-drag');
			slider_el.find('.owl-stage-outer').children().unwrap();
			return;
		}

		// Init
		slider_el.addClass( 'owl-carousel owl-theme' );
		slider_el.owlCarousel( options );
	} // FNC

	// inits or destroys the slider only when screen width changes
	document.addEventListener( 'designChangeTablet', () => {
		initOrDestroySlider();
	} );

	// init slider on page load
	initOrDestroySlider();
	slider_el[0].yeSliderInitiated = true;
} // FNC

/**
 * Loads more content via AJAX on Home, 404 pages
 */
gh.simpleLoadMoreContent = ( ajax_data, loadMoreBtn, successCallbackStart, successCallbackEnd ) => {

	let loadMoreBtnText = loadMoreBtn.innerHTML;

	$.ajax( {
		type:       'POST',
		url:        frontendData.ajaxURL,
		data:       ajax_data,
		beforeSend: function() {
			loadMoreBtn.innerHTML = 'Загрузка...'
			loadMoreBtn.classList.add('pe-none');
		},
		success:    function( data, response ) {
			// run success callback before appending html
			if ( successCallbackStart ) successCallbackStart();

			const loadedContentWrapper = $( '#ajax-loaded-content' );
			loadedContentWrapper.append( data );
			const newlyAddedBlock = loadedContentWrapper.find( '.opacity-0' );

			// run success callback before opacity restore
			if ( successCallbackEnd ) successCallbackEnd();

			newlyAddedBlock.removeClass( 'opacity-0' );
		},
		fail:       function( data ) {
		},
		complete:   function( data, responce ) {
			loadMoreBtn.innerHTML = loadMoreBtnText;
			loadMoreBtn.classList.remove('pe-none');
			document.dispatchEvent( new CustomEvent( 'yadroAjaxPostsLoaded' ) );
		},
	} ); // AJAX
} // IF

gh.loadMoreContent = ( listingPageData, filterData = {} ) => {
	let loadMoreBtn = document.getElementById( 'load-more-btn' );
	let pageNumber = (typeof listingPageData.pageNumber === 'number')
		? listingPageData.pageNumber + 1
		: '';
	let action = listingPageData.action ? listingPageData.action : 'load_more_posts';
	let postsToLoad = listingPageData.posts ? listingPageData.posts.splice( yePostListingPage.offset ?? 0, listingPageData.amount ) : '';

	if ( filterData.posts ) {
		postsToLoad = filterData.posts.splice( 0, listingPageData.amount );
	}

	let ajax_data = {
		action:      action,
		page_number: pageNumber,
		posts:       postsToLoad,
		post_type:   listingPageData.postType,
		page_dir:    listingPageData.pageDir,
		category:    listingPageData.category,
		// category:    listingPageData.category || new URLSearchParams( window.location.search ).get( 'category' ),
		amount:      listingPageData.amount || '',
		filter_data: Object.keys( filterData ).length ? filterData : null,
		extra_data:  yePostListingPage.extraData,
	};

	loadMoreBtn ? loadMoreBtn.classList.remove( 'd-none' ) : '';

	// save load more button text
	listingPageData.loadMoreBtnText = loadMoreBtn ? loadMoreBtn.innerHTML : listingPageData.loadMoreBtnText;

	$.ajax( {
		type:       'POST',
		url:        frontendData.ajaxURL,
		data:       ajax_data,
		beforeSend: function() {
			if ( loadMoreBtn ) {
				loadMoreBtn.innerHTML = 'Загрузка...'
				loadMoreBtn.classList.add('pe-none');
			}
		},
		success:    function( data, responce ) {
			const loadedContentWrapper = $( listingPageData.ajaxDivSelector ? listingPageData.ajaxDivSelector : '#ajax-loaded-content' );

			if ( yePostListingPage.extraData.clearContainer || filterData.type && ! filterData.loadMoreRun) {
				loadedContentWrapper[ 0 ].innerHTML = '';
			}

			loadedContentWrapper.append( data );
			// loadedContentWrapper.find( '.opacity-0' ).each( ( i, el) => {
			// 	el.classList.remove( 'opacity-0' )
			// } );

			// find again the load more btn elem (needed if the button was added later by AJAX)
			loadMoreBtn = document.getElementById( 'load-more-btn' );

			if (! pageNumber && ! listingPageData.posts?.length ) {
				loadMoreBtn.classList.add( 'd-none' );
				if ( yeContentFilter ) {
					yeContentFilter.loadBtnRemoved = true;
				} // IF
			} else {
				listingPageData.pageNumber = pageNumber;
			}
		},
		fail:       function( data ) {
		},
		complete:   function( data, responce ) {
			loadMoreBtn.innerHTML = listingPageData.loadMoreBtnText;
			loadMoreBtn.classList.remove('pe-none');
			yePostListingPage.extraData.clearContainer = null;

			document.dispatchEvent( new CustomEvent( 'yadroAjaxPostsLoaded' ) );
		},
	} ); // AJAX

} // FNC

gh.initLoadMorePostsListingPage = (selector = '.load-more-btn') => {

	// init extraData property if it's not set already
	yePostListingPage.extraData = yePostListingPage.extraData ? yePostListingPage.extraData : {};
	// default load btn text
	yePostListingPage.loadMoreBtnText = 'Показать больше';
	/* Load More Posts functionality
	-------------------------------------------------------------------*/
	$(document).on( 'click', selector, function() {
		const btn = this;
		// if there is load part data then pass appropriate object in the fnc, or general obj

		if (yeContentFilter && yeContentFilter.postType !== 'all' ) {
			gh.loadMoreContent( yePostListingPage, yeContentFilter );
		} else {
			gh.loadMoreContent( yePostListingPage );
		}
	} ); // CLICK

	/* Remove load more button event
	-------------------------------------------------------------------*/
	document.addEventListener( 'yadroRemoveLoadMoreBtn', () => {
		const btn = document.getElementById( 'load-more-btn' );
		btn.classList.add( 'd-none' );
		// btn.yeShouldBeHidden = true;

		if ( yeContentFilter ) {
			yeContentFilter.loadBtnRemoved = true;
		} // IF
	} );

} // FNC

gh.initListingContentFilter = (elem) => {
	/* Init listing page Content filter functionality
	-------------------------------------------------------------------*/
	const contentFilter = $( elem );
	if ( !contentFilter.length ) return;

	// init default variables/properties
	yeContentFilter.posts = null;
	yeContentFilter.postType = 'all';
	const yePLPCopy = { ...yePostListingPage };
	yePLPCopy.category = Array.isArray(yePLPCopy.category) ? yePLPCopy.category : '';
	// let loadMoreBtn = document.getElementById( 'load-more-btn' );
	// yeContentFilter.loadMoreBtn = loadMoreBtn?.parentElement.innerHTML;

	// activate link on page load (All categories / category from URL get param)
	window.addEventListener( 'load', function() {
		// const get_category = new URLSearchParams(document.location.search).get('category');
		// we don't use GET parameters in URL for category anymore, now it comes from the variable
		const queryVars = JSON.parse(frontendData.wpQueryVars);
		let link;

		// if there is category in URL GET parameters
		if ( queryVars?.category ) {
	        link = contentFilter[ 0 ].querySelector( `.content-filter__link[data-category="${queryVars.category}"]` );
		} else if (queryVars?.event_type) {
			link = contentFilter[ 0 ].querySelector( `.content-filter__link[data-type="${queryVars.event_type}"]` );
		// if it's pure page load without URL params
		} else {
			link = contentFilter[ 0 ].querySelectorAll( '.content-filter__link' ).item(0);
		}

		// make active and animate the link
		gh.activateAccentUnderlineLink( link );

	} );

	// content filter functionality on link click
	contentFilter.on( 'click', '.content-filter__link', function(e) {
		const link = this;
		e.preventDefault();

		// filter is in loading state
		if (contentFilter[ 0 ].classList.contains('--loading')) return;

		// NAVIGATION AND ANIMATION FOR IT
		contentFilter.addClass( '--loading' );

		// make active and animate the link, and deactivate sibling links
		gh.activateAccentUnderlineLink( link, contentFilter[ 0 ] );

		// LOADING POSTS
		if (yeContentFilter.loadMoreRun) {
			yeContentFilter.posts = yeContentFilter.loadMoreRun = null;
		}

		// we can skip request for fetching posts (in AjaxLoaders.php)
		// needed if requests for post is set elsewhere (e.g. in the page template)
		yePostListingPage.extraData.skipPostsRequest = (this.getAttribute( 'data-posts-request' ) === 'skip') ? 1 : 0;

		// get "page" name from the url
		const pageNameMatch = window.location.pathname.match( /^\/(\w+)/ );

		// actions by type of the filter
		switch (yeContentFilter.type) {
			case 'category':
				const category = this.getAttribute( 'data-category' );

				// change url when category/filter link is clicked
				if ( pageNameMatch && category ) {
					let breadcrumbs = document.querySelector('nav.breadcrumbs ol');
					let breadcrumbsChildren = breadcrumbs.querySelectorAll('li');

					if ( category !== 'all' ) {
						if ( breadcrumbsChildren.length < 3 ) {
							let lastCrumb = breadcrumbs.lastChild;
							let addCrumb = document.createElement('a');
							addCrumb.href = window.location.href;
							addCrumb.innerText = lastCrumb.innerText;
							let addLi = document.createElement('li');
							addLi.append(addCrumb);
							breadcrumbs.insertBefore(addLi, lastCrumb);
						}
						let catName = link.innerHTML;
						let crumb = document.querySelector('.breadcrumb_last');
						crumb.innerHTML = catName;
					} else if ( category === 'all' ) {
						if ( breadcrumbsChildren.length > 2 ) {
							let lastCrumb = breadcrumbs.lastChild;
							lastCrumb.remove();
							let currentCrumb = breadcrumbs.lastChild;
							let text = currentCrumb.innerText;
							currentCrumb.classList.add('breadcrumb_last');
							currentCrumb.innerText = text;
						}
					}

					const url = category === 'all' ? `/${pageNameMatch[ 1 ]}/` : `/${pageNameMatch[ 1 ]}/category/${category}/`;
					history.pushState( null, 'changing url to have category in it', url );
				}

				// reset some values to defaults after "tab" change
				yePostListingPage.pageNumber = 0;
				yePostListingPage.category = Array.isArray(yePLPCopy.category) ? [ ...yePLPCopy.category ] : yePLPCopy.category;

				if (category !== 'all') {

					const categoryObj = {
						taxonomy: yePostListingPage.extraData.taxonomy ? yePostListingPage.extraData.taxonomy : 'main_category',
						field:    'slug',
						terms:    category,
					} // OBJ

					if (Array.isArray(yePostListingPage.category)) {
						yePostListingPage.category.push(categoryObj);
					} else {
						yePostListingPage.category = category;
					}
				} // IF
				break;
			case 'events':
				// reset some values to defaults after "tab" change
				yePostListingPage.pageNumber = 0;

				yePostListingPage.extraData.eventsType = this.getAttribute( 'data-type' );
				yePostListingPage.extraData.clearContainer = true;

				if ( pageNameMatch && yePostListingPage.extraData.eventsType ) {
					const url = yePostListingPage.extraData.eventsType !== 'past' ? `/${pageNameMatch[ 1 ]}/` : `/${pageNameMatch[ 1 ]}/${yePostListingPage.extraData.eventsType}/`;
					history.pushState( null, 'changing url to have category in it', url );
				}

				break;
			default: // post-type-from-list - case
				yeContentFilter.postType = this.getAttribute( 'data-type' );
				yePostListingPage.posts = [...yeContentFilter.allPosts];
				break;
		} // SWITCH

		// All posts
		if (yeContentFilter.postType === 'all') {
			yeContentFilter.loadBtnRemoved = false;
			yePostListingPage.extraData.clearContainer = true;


			gh.loadMoreContent( yePostListingPage, {} );
		} else {
			gh.loadMoreContent( yePostListingPage, yeContentFilter );
		}

	} );

	// when post loading function completed
	document.addEventListener( 'yadroAjaxPostsLoaded', () => {
		contentFilter.removeClass( '--loading' );
	} );
} // FNC

gh.animAccentUnderline = (elem) => {
	// if .--active - return
	// if (elem.classList.contains('--active')) return;

	// set animation parameters only once
	if (! elem.yeAnimInitiated) {
		elem.yeAnimTimeLine   = gsap.timeline({ paused: true } );

		elem.yeAnimInitiated = true;
		elem.yeAnimTimeLine.to( elem, { '--line-bottom': '0%', duration: 0.3, ease: "power1.out" } );
		elem.yeAnimTimeLine.to( elem, { '--line-height': (elem.offsetHeight * 1.07), duration: 0.3, ease: "power1.out" },'-=0.3' );
		let animWidth = elem.offsetWidth - 1;
		elem.yeAnimTimeLine.to( elem, { '--line-width': animWidth, color: '#fff', duration: 0.45, ease: "power1.out" } )
	} // IF
	elem.yeAnimTimeLine.play();
}


gh.activateAccentUnderlineLink = (link, parent) => {

	// activate just one link
	if ( ! parent ) {
		link.classList.add( '--freeze' );
		gh.animAccentUnderline(link);

	} else { // activate one link and deactivate the other sibling links
		parent.querySelectorAll( 'a' ).forEach( ( elem ) => {
			if ( elem.classList.contains( '--freeze' ) ) {
				elem.classList.remove( '--freeze' );
				elem.yeAnimTimeLine?.reverse()
			}
		} );
		setTimeout( () => {
			link.classList.add( '--freeze' );
			if ( gh.isMobileDevice ) {
				gh.animAccentUnderline(link);
			}
		}, 50 );

	} // IF


} // FNC

/**
 * Hides meta tooltips on the site if they don't overlay the meta text
 */
gh.hideMetaTooltip = () => {
	const mainMetaTooltip = document.querySelectorAll('.main-meta-tooltip');

	mainMetaTooltip.forEach( (tooltip) => {
		const metaElem = tooltip.parentElement.querySelector( '.post-main-meta-iw-child' );
		if (! metaElem) {
			return;
		}
		const metaRect = metaElem.getBoundingClientRect(),
		      tooltipRect = tooltip.getBoundingClientRect();
		if ( (metaRect.left + metaRect.width) < tooltipRect.left ) {
			tooltip.parentElement.classList.add( 'hide-meta-tooltip' );
		} else {
			tooltip.parentElement.classList.remove( 'hide-meta-tooltip' );
		}
	} );

}


/**
 * Form validation for required fields of CF7
 */
gh.initCF7FrontendValidation = () => {

	function showFieldValidatonNotice(input, noticeText) {
		if ( input.parentElement.querySelector('.wpcf7-not-valid-tip') ) return;

		const notice = $('<span class="wpcf7-not-valid-tip" aria-hidden="true"></span>').text(noticeText );
		$(input).after( notice );
		input.yeCurrentNoticeElem = notice;
	}

	/**
	 * Returns true if required fields are not empty
	 */
	function checkReqFields(formWrap) {
		let validFields = true;
		const inputTypes = ['text', 'email'];

		formWrap.querySelectorAll( '.wpcf7-validates-as-required' ).forEach( (input) => {
			if ( inputTypes.includes( input.type ) ) {
				if (! input.value ) {
					validFields = false;
					showFieldValidatonNotice( input, 'Поле обязательно для заполнения.' );
				} else if ( input.type === 'email' && ! gh.validateEmailAddress(input.value ) ) {
					showFieldValidatonNotice( input, 'Введите корректный email адрес.' );
					validFields = false;
				}
			}
		} );
		return validFields;
	}

	document.addEventListener( 'yadroCf7AjaxLoaded', () => {
		document.querySelectorAll( '.wpcf7' ).forEach( ( formWrap ) => {

			// if the code below is already initiated return
			if (formWrap.yeFormValidationInitiated) return;

			// disable form submission by pressing Enter key
			$(formWrap).on( 'keydown', function(e) {
				if(e.keyCode === 13){
					e.preventDefault();
				}
			} );

			$(formWrap).on( 'click', '.fake-submit-btn', function(e) {
				if (checkReqFields(formWrap)) {
					submitBtn.click();
				}
			} );

			$(formWrap).on( 'change', '.wpcf7-validates-as-required', function() {
				if ( this.yeCurrentNoticeElem ) {
					this.yeCurrentNoticeElem.remove();
					this.yeCurrentNoticeElem = null;
				}
			} );

			const submitBtn     = formWrap.querySelector( '.wpcf7-submit' ),
			      fakeSubmitBtn = submitBtn.cloneNode( true );
			fakeSubmitBtn.type = 'button';
			fakeSubmitBtn.classList.add( 'fake-submit-btn' );
			submitBtn.after( fakeSubmitBtn );
			
			formWrap.yeFormValidationInitiated = true;
		} ); // EACH FORM
	} );
} // FNC


/**
 * Init functionality for "like" and "copy link" buttons + for updating likes/views
 */
gh.initExtraBottomButtons = ( type = 'post' ) => {
	let rest_route = type === 'post' ? '' : '-taxonomy';

	// Update/Add view and likes functionality
	if (Number.isInteger(statisticId)) {
		let id = statisticId;

		fetch(`/wp-json/engineer/v1/statistic${rest_route}/get/${id}`, {method: 'GET'})
			.then(res => res.json())
			.then(response => {
				if (response?.likes) {
					document.querySelectorAll('.post-x-meta__likes .counter, button[data-the-id="' + id + '"] .like-post-btn__text').forEach(el => {
						el.textContent = response.likes;
					})
				}
				// TODO Check that we don't have any other views on the pages
				if (response?.views) {
					document.querySelectorAll('.post-x-meta__views .counter').forEach(el => {
						el.textContent = response?.views;
					})
				}
			});
	} // IF


	// Buttons functionality
	const btnsWrap = $( '.extra-bottom-btns-wrap' );

	if ( btnsWrap.length && ! btnsWrap[ 0 ].yeButtonInited ) {
		btnsWrap.on( 'click', '.extra-bottom-btn', function() {
			// general for all the buttons
			const btn = this;

			// inner functions
			const buttonClickedSuccess = () => {
				btn.classList.add( 'active', 'pe-none' );
				setTimeout( () => {
					btn.classList.remove( 'active', 'pe-none' );
				}, 4000 );
			}

			// copy link btn
			if ( btn.classList.contains( 'copy-post-link-btn' ) ) {
				navigator.clipboard.writeText( window.location.href ).then( () => {
					buttonClickedSuccess();
				} );
			} // IF

			const id = btn.getAttribute( 'data-the-id' );

			// like btn
			if ( btn.classList.contains( 'like-post-btn' ) ) {
				buttonClickedSuccess();

				fetch( `/wp-json/engineer/v1/likes${rest_route}/${id}`, { method: 'POST' } )
					.then( response => response.json() )
					.then( likes => {
						if ( likes !== parseInt( btn.querySelector( '.like-post-btn__text' ).textContent ) ) {
							// btn.querySelector( '.like-post-btn__text' ).textContent = likes;
							document.querySelectorAll( '.like-post-btn__text' ).forEach( el => {
								el.textContent = likes;
							} );
							document.querySelectorAll( '.post-x-meta__likes .counter' ).forEach( el => {
								el.textContent = likes;
							} );
						} else {
							btn.querySelector( '.extra-bottom-btn__notice-txt' ).classList.remove( 'opacity-0' );
						}

						btn.classList.add( '--liked' );
					} );
			} // IF

		} ); // ON CLICK
		// init button flag
		btnsWrap[ 0 ].yeButtonInited = true;
	}

} // FNC


/**
 * Inits Authors slider
 */
gh.initAuthorsSlider = ( $selector = '.author-slider' ) => {
	document.querySelectorAll( $selector ).forEach( function( slider ) {
		if ( slider.yeSliderInited ) return;

		const authorSlider = $(slider).owlCarousel({
			margin: 0,
			dots: false,
			nav: false,
			loop: true,
			animateOut: 'fadeOut',
			mouseDrag: false,
			items: 1,
			smartSpeed: 5000,
		});

		authorSlider.on('click', 'a', function (e) {

			if (e.target.classList.contains('author-slider__btn')) {
				e.preventDefault();
				authorSlider.trigger('next.owl.carousel')
			} // IF
		});

		// fixes strange bug on resolutions between 1200-1300px
		if (gh.isTabletDesign) {
			authorSlider.trigger('refresh.owl.carousel');
		} // IF

		authorSlider.removeClass( 'opacity-0' );
		slider.yeSliderInited = true;

	} ); // EACH

} // FNC



})(jQuery)